import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Home, ListChecks, PlusCircle, Settings, LogOut, LayoutDashboard,
  Calendar, PlusSquare, PieChart, Building2, RotateCcw, Receipt, Table, Users, Store, BellRing,
  Camera, CalendarDays, BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const salesItems = [
  { to: "/", label: "Ana Sayfa", Icon: Home, testId: "nav-home", end: true },
  { to: "/calls", label: "Tüm Çağrılar", Icon: ListChecks, testId: "nav-calls" },
  { to: "/calls/new", label: "Yeni Çağrı Ekle", Icon: PlusCircle, testId: "nav-new" },
  { to: "/settings", label: "Ayarlar", Icon: Settings, testId: "nav-settings" },
];

const financeItems = [
  { to: "/finance", label: "Genel Özet (Ana Sayfa)", Icon: LayoutDashboard, testId: "fnav-dashboard", end: true },
  { to: "/finance/recurring-jobs/new", label: "Yeni Tekrar Eden İş Ekle", Icon: Calendar, testId: "fnav-recurring-new" },
  { to: "/finance/onetime-jobs/new", label: "Yeni İş Ekle", Icon: PlusSquare, testId: "fnav-onetime-new" },
  { to: "/finance/monthly-billing", label: "Aylık Toplam Bilanço", Icon: PieChart, testId: "fnav-monthly" },
  { to: "/finance/payments-due", label: "Ödeme Bildirimi", Icon: BellRing, testId: "fnav-payments-due" },
  { to: "/finance/firm-billing", label: "Firma Bilanço Takip", Icon: Building2, testId: "fnav-firm" },
  { to: "/finance/firms", label: "Firmalar (Düzenle/Sil)", Icon: Store, testId: "fnav-firms" },
  { to: "/finance/recurring-expenses/new", label: "Sürekli Gider Ekle", Icon: RotateCcw, testId: "fnav-rec-exp" },
  { to: "/finance/onetime-expenses/new", label: "Tek Seferlik Gider Ekle", Icon: Receipt, testId: "fnav-ot-exp" },
  { to: "/finance/monthly-expenses", label: "Aylık Gider Tablosu", Icon: Table, testId: "fnav-monthly-exp" },
  { to: "/finance/personnel", label: "Personel", Icon: Users, testId: "fnav-personnel" },
];

const shootItems = [
  { to: "/shoots", label: "Takvim", Icon: CalendarDays, testId: "snav-schedule", end: true },
  { to: "/shoots/photographers", label: "Çekimciler", Icon: Camera, testId: "snav-photographers" },
  { to: "/shoots/stats", label: "İstatistikler", Icon: BarChart3, testId: "snav-stats" },
];

function NavItem({ item, activeColor }) {
  const { to, label, Icon, testId, end } = item;
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
          isActive
            ? activeColor === "green"
              ? "bg-emerald-800 text-white"
              : activeColor === "purple"
                ? "bg-violet-700 text-white"
                : "bg-orange-50 text-orange-600"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={17} className={isActive ? (activeColor === "green" ? "text-white" : activeColor === "purple" ? "text-white" : "text-orange-500") : "text-slate-400"} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <aside className="w-[280px] h-screen fixed left-0 top-0 border-r border-slate-200 flex flex-col bg-[#FDFDFD]" data-testid="sidebar">
      <div className="px-5 pt-6 pb-4">
        <BrandLogo size="md" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 px-3 pb-2 pt-3">Satış CRM</div>
        <nav className="flex flex-col gap-1">
          {salesItems.map((it) => <NavItem key={it.to} item={it} activeColor="orange" />)}
        </nav>

        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 px-3 pb-2 pt-6">Finans</div>
        <nav className="flex flex-col gap-1">
          {financeItems.map((it) => <NavItem key={it.to} item={it} activeColor="green" />)}
        </nav>

        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 px-3 pb-2 pt-6">Çekim Programı</div>
        <nav className="flex flex-col gap-1">
          {shootItems.map((it) => <NavItem key={it.to} item={it} activeColor="purple" />)}
        </nav>
      </div>

      <div className="mx-4 my-4 pt-4 border-t border-slate-200 flex items-center gap-3" data-testid="sidebar-user">
        <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm">
          {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{user?.name || "Kullanıcı"}</div>
          <div className="text-xs text-slate-500 truncate">Satış Yetkilisi</div>
        </div>
        <button onClick={handleLogout} data-testid="logout-btn" className="text-slate-400 hover:text-slate-900 transition-colors p-1" title="Çıkış Yap">
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
